import { makePointer } from '../../../api/_utils'
import { useGetProfessionFromLabelLazyQuery } from '../../../api/queries/__generated__/profession.gql'
import { useGetQualificationFromLabelLazyQuery } from '../../../api/queries/__generated__/qualification.gql'
import { useGetSpecialityFromLabelLazyQuery } from '../../../api/queries/__generated__/speciality.gql'
import { GetUserDataPointersPointersResult, UserData } from '../_props'

const extractObjectIdAndMakePointer = (edges: any[] | undefined, pointerClassname: string) => {
    const objectId = edges ? edges[0]?.node?.objectId : undefined

    if (objectId) {
        return makePointer(pointerClassname, objectId)
    }
    return undefined
}

const useGetUserDataPointers = () => {
    const [getProfessionFromLabelQuery] = useGetProfessionFromLabelLazyQuery()
    const [getQualificationFromLabelQuery] = useGetQualificationFromLabelLazyQuery()
    const [getSpecialityFromLabelQuery] = useGetSpecialityFromLabelLazyQuery()

    const getUserDataPointers = async ({
        professionPointer,
        qualificationPointer,
        specialityPointer,
        speciality,
        profession,
        qualification,
        speMentions,
    }: UserData): Promise<GetUserDataPointersPointersResult> => {
        const builtSpeMentionPointer =
            speMentions?.length > 0 ? speMentions.map((m) => makePointer('SpeMention', m.id)) : undefined

        const response: GetUserDataPointersPointersResult = {
            professionPointer,
            qualificationPointer,
            speMentionPointer: builtSpeMentionPointer,
            specialityPointer,
        }

        if (
            Object.values(response).filter((x) => !Array.isArray(x) && x !== undefined).length ===
            Object.values(response).length
        )
            return response

        if (!professionPointer && profession) {
            const res = await getProfessionFromLabelQuery({
                variables: {
                    professionLabel: profession,
                },
            })

            response.professionPointer = extractObjectIdAndMakePointer(
                res.data?.professions.edges ?? undefined,
                'Profession',
            )
        }

        if (!qualificationPointer && qualification) {
            const res = await getQualificationFromLabelQuery({
                variables: {
                    qualificationLabel: qualification,
                },
            })

            response.qualificationPointer = extractObjectIdAndMakePointer(
                res.data?.qualifications.edges ?? undefined,
                'Qualification',
            )
        }

        if (!specialityPointer && speciality) {
            const res = await getSpecialityFromLabelQuery({
                variables: {
                    specialityLabel: speciality,
                },
            })

            response.specialityPointer = extractObjectIdAndMakePointer(
                res.data?.specialities.edges ?? undefined,
                'Speciality',
            )
        }

        // filter out specialityPointer if it doesn't have a valid id
        if (specialityPointer && typeof response.specialityPointer === 'object') {
            const pointer = response.specialityPointer as any
            if (
                !pointer.objectId ||
                pointer.objectId === '' ||
                pointer.id === '' ||
                (pointer.__type === 'Pointer' && !pointer.objectId)
            ) {
                response.specialityPointer = undefined
            }
        }

        if (!response.professionPointer) {
            throw new Error('Error: missing profession')
        }

        return response
    }

    return {
        getUserDataPointers,
    }
}

export default useGetUserDataPointers
